import moment from "moment";
import React, { useState, useEffect } from "react";
import TableCell from "@mui/material/TableCell";
import { Avatar } from "@mui/material";
import TableRow from "@mui/material/TableRow";
import CustomTable from "../../Components/Table";
import { Button, Input, Modal, Switch } from "antd";
import { timesheetStatus } from "../../Utils/constants";
import Swal from "sweetalert2";
import { Link, useParams } from "react-router-dom";
import ShareNavbar from "../../Components/ShareNavbar";
import PublicTimesheetProvider, {
  usePublicTimesheetProvider,
} from "../../Providers/Public/Timesheet";
import { stringAvatar } from "../../Utils/avatar";
import isEmail from "validator/lib/isEmail";
import { InfinitySpin } from "react-loader-spinner";
import CryptoJS from "crypto-js";
import Sidebar from "./Sidebar";

const ShareTimesheet = () => {
  // Initial Setup
  const { token } = useParams();
  const [emailFromToken, setEmailFromToken] = useState(null);
  const [linkId, setLinkId] = useState(null);

  useEffect(() => {
    if (token) {
      const secret = "rJK7?E@KynQ!5F39C-gy";
      const wordArray = CryptoJS.enc.Base64url.parse(token);
      const string = wordArray.toString(CryptoJS.enc.Base64);
      const bytes = CryptoJS.AES.decrypt(string, secret);
      const data = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
      setEmailFromToken(data.email);
      setLinkId(data.linkId);
    }
  }, [token]);

  // PIN
  const [isMounted, setIsMounted] = useState(false);
  const [pinRequired, setPinRequired] = useState(true);
  const [pin, setPin] = useState("");

  // Check if pin is required
  const { isPinRequired, verifyPin } = usePublicTimesheetProvider();
  useEffect(() => {
    if (linkId)
      isPinRequired(linkId).then((res) => {
        if (res !== null) {
          setIsMounted(true);
          setPinRequired(res);
        } else {
          Swal.fire("Error", "Something went wrong", "error");
        }
      });
  }, [linkId]);

  // Timesheet status
  const { timesheets } = usePublicTimesheetProvider();
  const [status, setStatus] = useState(null);
  const [weeklySummary, setWeeklySummary] = useState(false);
  const [summaryWeek, setSummaryWeek] = useState();
  const [approvalDetails, setApprovalDetails] = useState(null);

  useEffect(() => {
    if (timesheets?.every((t) => t.status === timesheets[0].status)) {
      setStatus(timesheets[0].status);
      setApprovalDetails({
        approvalDate: timesheets[0].approvalDate,
        approvedByExternal: timesheets[0].approvedByExternal,
        approved_by: timesheets[0].approvedBy,
        clientComments: timesheets[0]?.clientComments,
      });
    }
  }, [timesheets]);

  // Timesheet
  const { timesheetMaster, days } = usePublicTimesheetProvider();

  const weeklyTotalHours = (data) => {
    let totalHours = 0, minutes = 0;

    if (data) {
      for (let i = 0; i < data.length; i++) {
        minutes += parseInt(data[i].minutes);
        totalHours += parseInt(data[i].hours);
      }
      if (minutes >= 60) {
        totalHours = totalHours + parseInt(minutes / 60);
        minutes = minutes % 60;
      }

      return { totalHours, minutes }
    }
  }

  function convertToHHMM(time) {
    const [hour, minute] = time.split(':');
    const formattedHour = hour.padStart(2, '0');
    const formattedMinute = minute.padStart(2, '0');
    return `${formattedHour}:${formattedMinute}`;
  }

  useEffect(() => {

    let newDaysArray = []

    for (let i = 0; i < days.length; i = i + 7) {
      const { totalHours, minutes } = weeklyTotalHours(days)

      newDaysArray.push({
        id: days[i].id,
        week: moment(days[i].date).format("LL") + " - " + moment(days[i + 6].date).format("LL"),
        hours: convertToHHMM(totalHours + ":" + minutes),
        description: ""
      })

    }
    setSummaryWeek(newDaysArray)

  }, [days])

  const headCells = ["Date", "Hours", "Description"].map((label) => ({ label })); // prettier-ignore
  const summaryHeadCells = ["Week", "Hours", "Description"].map((label) => ({ label }));

  const CustomRow = ({ row }) => {
    return (
      <TableRow
        key={row.id}
        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
      >
        <TableCell width="20%" component="th" scope="row">
          {moment.utc(row.date).format("dddd, MMMM DD, YYYY")}
        </TableCell>
        <TableCell width="20%">
          {row.hours.toString().padStart(2, "0")}:
          {row.minutes.toString().padStart(2, "0")}
        </TableCell>
        <TableCell className="truncate max-w-[250px] grow">{row.description}</TableCell>
      </TableRow>
    );
  };

  const CustomSummaryRow = ({ row }) => {
    return (
      <TableRow
        key={row.id}
        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
      >
        <TableCell width="20%" component="th" scope="row">
          {row.week}
        </TableCell>
        <TableCell width="20%">
          {row.hours}
        </TableCell>
        <TableCell className="truncate max-w-[250px] grow">{row.description}</TableCell>
      </TableRow>
    );
  };

  // Modal
  const { approveTimesheet, rejectTimesheet } = usePublicTimesheetProvider();
  const [comments, setComments] = useState("");
  const [email, setEmail] = useState("");
  const [modal, setModal] = useState({
    open: false,
    type: timesheetStatus.approved,
  });

  return (
    <div
      style={{ background: "#F7F7F7" }}
      className="flex flex-col min-h-screen"
    >
      <ShareNavbar />
      {isMounted ? (
        <div className="flex grow">
          <Sidebar />
          <div
            className={`flex flex-col grow p-8 ${pinRequired ? "items-center justify-center" : ""
              }`}
          >
            {pinRequired ? (
              <div className="px-8 py-4 min-w-[35vw] min-h-[40vh] flex flex-col items-center space-y-8 justify-center bg-white rounded-xl">
                <span className="flex text-justify">
                  Enter the secured PIN to access the timesheet
                </span>
                <Input
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  className="min-w-[240px] w-2/3 md:w-1/2 xl:w-2/5 2xl:w-1/3 mx-auto"
                  placeholder="Enter Pin"
                  maxLength="4"
                />
                <div className="min-w-[240px] w-2/3 md:w-1/2 xl:w-2/5 2xl:w-1/3 px-16">
                  <Button
                    className="w-full text-white rounded-sm"
                    style={{ backgroundColor: "#2EBAA3" }}
                    type="primary"
                    onClick={() =>
                      verifyPin(linkId, pin).then((res) => {
                        if (res !== null) {
                          if (res === true) setPinRequired(false);
                          else Swal.fire("Error", "Invalid PIN", "error");
                        } else
                          Swal.fire("Error", "Something went wrong", "error");
                      })
                    }
                  >
                    Verify
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex justify-between mb-2">
                  <div className="flex items-center">
                    <span className="text-2xl font-semibold">Timesheet</span>
                  </div>
                  <div className="space-x-2 py-4">
                    {status === timesheetStatus.approved ||
                      status === timesheetStatus.rejected ? (
                      <div
                        className="px-2 py-1 rounded-[5px] font-[18px] leading-[22px]"
                        style={
                          status === timesheetStatus.approved
                            ? { backgroundColor: "#2EBAA34D", color: "#2EBAA3" }
                            : { background: "#FF33334D", color: "#FF3333" }
                        }
                      >
                        {status?.[0] + status?.slice(1)?.toLowerCase()}
                      </div>
                    ) : (
                      <>
                        <button
                          className="px-2 py-1.5 text-[#139588] rounded-md border border-[#139588] w-[100px]"
                          onClick={() =>
                            setModal({
                              open: true,
                              type: timesheetStatus.rejected,
                            })
                          }
                        >
                          Reject
                        </button>
                        <button
                          className="bg-[#2EBAA3] text-white px-2 py-1.5 rounded-md border border-[#2EBAA3] w-[100px]"
                          onClick={() =>
                            setModal({
                              open: true,
                              type: timesheetStatus.approved,
                            })
                          }
                        >
                          Approve
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Consultant */}
                {timesheetMaster?.consultant ? (
                  <div className="space-y-4">
                    <div
                      style={{ border: "1px solid #D9D9D9" }}
                      className="flex flex-col bg-white p-4 rounded-md"
                    >
                      <div className="flex flex-row space-x-4">
                        <span style={{ fontWeight: "500" }} className="">
                          Title
                        </span>
                        <span>
                          {timesheetMaster?.job?.name ??
                            timesheetMaster?.job?.name ??
                            "-"}
                        </span>
                      </div>
                      <div className="flex flex-row space-x-4">
                        <span style={{ fontWeight: "500" }} className="">
                          Description
                        </span>
                        <span>
                          {timesheetMaster?.job?.description
                            ? timesheetMaster?.job?.description
                            : ""}
                        </span>
                      </div>
                    </div>
                    <div
                      style={{
                        border: "1px solid #D9D9D9",
                        borderRadius: "8px",
                      }}
                      className="flex flex-row justify-between bg-white rounded-sm p-4 mb-8"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">Consultant</span>
                        <div className="flex flex-col bg-white flex-1 rounded-b-lg mt-2">
                          <div
                            style={{
                              width: "287px",
                              height: "42px",
                            }}
                            className="flex flex-row items-center space-x-6 bg-[#2EBAA3] rounded-lg"
                          >
                            <Link
                              to={`/consultant/profile/${timesheetMaster?.consultant &&
                                timesheetMaster.consultant.id
                                }`}
                            >
                              <Avatar
                                className="ml-2"
                                style={{
                                  height: "35px",
                                  width: "35px",
                                }}
                                {...stringAvatar(
                                  `${timesheetMaster?.consultant &&
                                  timesheetMaster.consultant.firstName +
                                  " " +
                                  timesheetMaster.consultant.lastName
                                  }`
                                )}
                              />
                            </Link>
                            <span className="text-white">
                              {timesheetMaster?.consultant &&
                                timesheetMaster.consultant.firstName}{" "}
                              {timesheetMaster?.consultant &&
                                timesheetMaster.consultant.lastName}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-col">
                  {status === timesheetStatus.approved ||
                    status === timesheetStatus.rejected ? (
                    <>
                      <div className="flex mt-4 mb-1 space-x-2">
                        <span className="font-semibold">{status === timesheetStatus.approved ? "Approved By:" : "Rejected By:"}</span>
                        <span>
                          {approvalDetails?.approvedByExternal ??
                            approvalDetails?.approvedBy?.email}
                        </span>
                      </div>

                      <div className="flex my-1 space-x-2">
                        <span className="font-semibold">Approved On:</span>
                        <span>
                          {moment(
                            approvalDetails?.approvalDate ?? undefined
                          ).format("dddd, MMMM DD, HH:MM")}
                        </span>
                      </div>

                      <div className="flex mt-1 mb-2 space-x-2">
                        <span className="font-semibold">Comments:</span>
                        <span>{approvalDetails?.clientComments}</span>
                      </div>
                    </>
                  ) : (
                    <div className="mb-6" />
                  )}

                  <div className="flex font-semibold mb-4 space-x-2">
                    <span>Total Hours: </span>
                    <span className="text-[#139588]">
                      {timesheetMaster?.hours?.toString()?.padStart(2, "0")}:
                      {timesheetMaster?.minutes?.toString()?.padStart(2, "0")}
                    </span>
                  </div>
                </div>

                <div className="mb-4 flex flex-row space-x-2 items-center">
                  <span className="border-2 border-[#139588] py-1 px-4 text-[#139588] text-xs">Day</span>
                  <Switch className="w-8 bg-gray-700" onClick={() => {
                    setWeeklySummary(!weeklySummary)
                  }} />
                  <span className="border-2 border-[#139588] py-1 px-4 text-[#139588] text-xs">Week</span>
                </div>

                {/* Timesheet Details */}
                <CustomTable
                  headCells={weeklySummary ? summaryHeadCells : headCells}
                  rows={weeklySummary ? summaryWeek : days}
                  CustomRow={weeklySummary ? CustomSummaryRow : CustomRow}
                  showPagination={false}
                  showDensePadding={false}
                  showToolbar={false}
                />

                {/* Modal */}
                <Modal
                  open={modal.open}
                  onCancel={() =>
                    setModal({ open: false, type: timesheetStatus.approved })
                  }
                  okButtonProps={{ className: "bg-[#1677FF]" }}
                  okText={
                    modal.type === timesheetStatus.rejected
                      ? "Reject"
                      : "Approve"
                  }
                  closable={false}
                  onOk={() => {
                    if (modal.type === timesheetStatus.rejected) {
                      if (comments.length === 0) {
                        Swal.fire(
                          "Error",
                          "Please enter a reason for rejection",
                          "error"
                        );
                      } else if (!isEmail(emailFromToken ?? email)) {
                        Swal.fire(
                          "Error",
                          "Please enter a valid email address",
                          "error"
                        );
                      } else if (timesheetMaster?.id) {
                        rejectTimesheet(
                          timesheetMaster.id,
                          comments,
                          emailFromToken ?? email
                        ).then(() => {
                          setModal({
                            open: false,
                            type: timesheetStatus.approved,
                          });

                          setApprovalDetails({
                            approvalDate: new Date(),
                            approvedByExternal: emailFromToken ?? email,
                            approved_by: null,
                            clientComments: comments,
                          });
                        });
                      }
                    } else {
                      if (!isEmail(emailFromToken ?? email)) {
                        Swal.fire(
                          "Error",
                          "Please enter a valid email address",
                          "error"
                        );
                      } else if (timesheetMaster?.id) {
                        approveTimesheet(
                          timesheetMaster?.id,
                          comments,
                          emailFromToken ?? email
                        ).then(() => {
                          setModal({
                            open: false,
                            type: timesheetStatus.approved,
                          });

                          setApprovalDetails({
                            approvalDate: new Date(),
                            approvedByExternal: emailFromToken ?? email,
                            approved_by: null,
                            clientComments: comments,
                          });
                        });
                      }
                    }
                  }}
                >
                  <p className="font-medium mb-1 ml-0.5">Email</p>
                  <input
                    className="w-full text-sm mb-3"
                    maxLength={100}
                    type="email"
                    disabled={Boolean(emailFromToken)}
                    value={emailFromToken ?? email}
                    onChange={
                      emailFromToken ? null : (e) => setEmail(e.target.value)
                    }
                  />

                  <p className="font-medium mb-1 ml-0.5">Comments</p>
                  <input
                    className="w-full text-sm mb-3"
                    maxLength={250}
                    onChange={(e) => setComments(e.target.value)}
                  />
                </Modal>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col grow items-center justify-center">
          <InfinitySpin color="#2EBAA3" />
        </div>
      )}
    </div>
  );
};

const ShareTimesheetWrapper = () => (
  <PublicTimesheetProvider>
    <ShareTimesheet />
  </PublicTimesheetProvider>
);

export default ShareTimesheetWrapper;
